import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { 
         IOpsViewerMessagesService,
         IApplicationContextService} from '../../../services/services';
import {  WellKnownMeasurementChoice } from '../../../models/models';
import { SelectItem } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { CompanyNameViewModel, Device, DeviceClient, TcpDeviceConfiguration, CompanyWorkbookConfigurationClient } from '../../../services/nswag/nswagclient';
import { map } from 'rxjs/operators';
import { DeviceCategories } from '../../../models/deviceCategories';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'device-detail',
    standalone: false,
    
    templateUrl: './device-detail.component.html',
    styleUrls: ['./device-detail.component.css']
})

export class DeviceDetailComponent {
    device$: Observable<Device>;
    device: Device = new Device();
    public deviceCategories: SelectItem[] = [];
    public isNew = false;
    public modelInvalid = false;
    public showModelInvalidDialog = false;
    public validationErrors: string[] = [];
    private readonly deviceIdPattern = /^[A-Za-z0-9-]+$/;

    public currentCompany$: Observable<CompanyNameViewModel | null>;

    wellKnownMeasurements$: Observable<WellKnownMeasurementChoice[]>;


    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _deviceService: DeviceClient,
        private _titleService: Title,
        private _companyConfigurationClient: CompanyWorkbookConfigurationClient,
        private _messageService: IOpsViewerMessagesService,
        private _applicationContextService: IApplicationContextService) { 
            this.setDeviceCategories();
            this.wellKnownMeasurements$ = this._companyConfigurationClient.getWellKnownMeasurements().pipe(
                map(results => (results || []).map(r => ({
                    name: r.name ?? '',
                    displayName: r.displayName ?? (r.name ?? ''),
                    siUnits: (r.siUnits || []).map(u => ({ unitKey: u.unitKey, unitName: u.unitName })),
                    imperialUnits: (r.imperialUnits || []).map(u => ({ unitKey: u.unitKey, unitName: u.unitName })),
                    defaultSiUnit: r.defaultSiUnit ? { unitKey: r.defaultSiUnit.unitKey, unitName: r.defaultSiUnit.unitName } : null,
                    defaultImperialUnit: r.defaultImperialUnit ? { unitKey: r.defaultImperialUnit.unitKey, unitName: r.defaultImperialUnit.unitName } : null
                }) as WellKnownMeasurementChoice))
            );
            const deviceId = this._route.snapshot.paramMap.get('id');
            const decodedDeviceId = deviceId ? decodeURIComponent(deviceId) : null;
            if (decodedDeviceId && decodedDeviceId.toUpperCase() === 'NEW') {
                this.isNew = true;
                this.device = new Device();
                this.device$ = of(this.device);
            } else {
                this.device$ = this._deviceService.getById(decodedDeviceId ?? '').pipe(
                    map(device => {
                        this.device = device;
                        if (device.category === DeviceCategories.TCP && !device.tcpDeviceConfiguration) {
                            this.device.tcpDeviceConfiguration = new TcpDeviceConfiguration();
                        }
                        // Sanitize measurementChannelConfigurations to prevent null/undefined entries
                        if (this.device.measurementChannelConfigurations) {
                            this.device.measurementChannelConfigurations = this.device.measurementChannelConfigurations.filter(c => c != null);
                        } else {
                            this.device.measurementChannelConfigurations = [];
                        }
                        return this.device;
                    })
                );
            }
            this.setPageTitle(decodedDeviceId ?? '');
            this.currentCompany$ = this._applicationContextService.companyContextObserver;
        }

    

    setDeviceCategories() {
        this.deviceCategories = [];
        this.deviceCategories.push({ label: DeviceCategories.DPU, value: DeviceCategories.DPU });
        this.deviceCategories.push({ label: DeviceCategories.Ultralight, value: DeviceCategories.Ultralight });
        this.deviceCategories.push({ label: DeviceCategories.VisionDesktop, value: DeviceCategories.VisionDesktop });
        this.deviceCategories.push({ label: DeviceCategories.FlowBack, value: DeviceCategories.FlowBack });
        this.deviceCategories.push({ label: DeviceCategories.TCP, value: DeviceCategories.TCP });
    }

  

    setPageTitle(deviceId: string) {
        if (!deviceId || deviceId === 'New' || deviceId === '') {
          this._titleService.setTitle('New Job');
        } else { this._titleService.setTitle(deviceId); }
      }


    createNewDevice() {
        const id = uuidv4();
        this.device$ = new Observable<Device>(observer => {
            const newDevice = new Device();
            newDevice.id = id;
            this.device = newDevice;
            observer.next(newDevice);
            observer.complete();
        });
    }

    



    validateDeviceModel(device: Device): string[] {
        const errors: string[] = [];
        
        const trimmedId = device.id?.trim() ?? '';
        if (trimmedId === '') { 
            errors.push('Device ID is required and cannot be empty.'); 
        }
        if (trimmedId !== '' && !this.deviceIdPattern.test(trimmedId)) {
            errors.push('Device ID must be alphanumeric or "-" only (no spaces or "/").');
        }
        if (!device.name || device.name.trim() === '') { 
            errors.push('Device Name is required and cannot be empty.'); 
        }
        if (!device.category) { 
            errors.push('Device Category must be selected.'); 
        }
        
        // TCP Configuration validation
        if (device.category === DeviceCategories.TCP) {
            if (!device.tcpDeviceConfiguration) {
                errors.push('TCP Device Configuration is required when category is TCP.');
            } else {
                //split host and port by colon
                const [host, port] = device.tcpDeviceConfiguration.hostPort ? device.tcpDeviceConfiguration.hostPort.split(':') : [];
                if (!host || host.trim() === '') {
                    errors.push('TCP Host is required for TCP devices. Must be in the format "host:port".');
                }
                if (!port || port.trim() === '' || isNaN(Number(port)) || Number(port) <= 0 ) {
                    errors.push('TCP Port must be a valid positive number for TCP devices. Must be in the format "host:port');
                }
            }
        }
        
        if (device.measurementChannelConfigurations && device.measurementChannelConfigurations.length > 0) {
            for (let i = 0; i < device.measurementChannelConfigurations.length; i++) {
                const config = device.measurementChannelConfigurations[i];
                if (!config.measurementName || config.measurementName.trim() === '') { 
                    errors.push(`Measurement configuration at row ${i + 1} requires a measurement name.`); 
                }
            }
        }
        return errors;
    }

    private normalizeDeviceValue(value: string | null | undefined): string {
        return (value ?? '').trim().toLowerCase();
    }

    private validateDeviceUniqueness(device: Device): Observable<string[]> {
        const normalizedId = this.normalizeDeviceValue(device.id);
        const normalizedName = this.normalizeDeviceValue(device.name);
        if (!normalizedId && !normalizedName) {
            return of([]);
        }

        return this._deviceService.get(true).pipe(
            map(devices => {
                const errors: string[] = [];
                const deviceList = devices ?? [];
                const sameIdCount = normalizedId
                    ? deviceList.filter(d => this.normalizeDeviceValue(d.id) === normalizedId).length
                    : 0;
                const allowedIdCount = this.isNew ? 0 : 1;
                if (sameIdCount > allowedIdCount) {
                    errors.push(`Device ID "${device.id}" already exists.`);
                }

                if (normalizedName) {
                    const hasDuplicateName = deviceList.some(d =>
                        this.normalizeDeviceValue(d.name) === normalizedName &&
                        this.normalizeDeviceValue(d.id) !== normalizedId);
                    if (hasDuplicateName) {
                        errors.push(`Device Name "${device.name}" already exists.`);
                    }
                }
                return errors;
            })
        );
    }

    // Add method to handle category changes
    onCategoryChange() {
        if (this.device.category === DeviceCategories.TCP && !this.device.tcpDeviceConfiguration) {
            this.device.tcpDeviceConfiguration = new TcpDeviceConfiguration();
        }
    }

    saveDevice() {
        const companyContext = this._applicationContextService.getCompanyContext();
        if (companyContext) {
            this.device.companyId = companyContext.companyId;
        }

        this.validationErrors = this.validateDeviceModel(this.device);
        this.modelInvalid = this.validationErrors.length > 0;
        this.showModelInvalidDialog = this.modelInvalid;

        if (this.showModelInvalidDialog) {
            return;
        }

        this.validateDeviceUniqueness(this.device).subscribe(
            errors => {
                this.validationErrors = errors;
                this.modelInvalid = this.validationErrors.length > 0;
                this.showModelInvalidDialog = this.modelInvalid;
                if (this.showModelInvalidDialog) {
                    return;
                }

                if (this.isNew) {
                    this._deviceService.post(this.device).subscribe(
                        result => { this.returnToDeviceList(); },
                        error => { this._messageService.errorMessage(error, 'Could not Create Device'); }
                    );
                } else {
                    const deviceId = this.device.id;
                    if (!deviceId) {
                        this.validationErrors = ['Device ID is required and cannot be empty.'];
                        this.modelInvalid = true;
                        this.showModelInvalidDialog = true;
                        return;
                    }
                    this._deviceService.put(deviceId, this.device).subscribe(
                        result => { this.returnToDeviceList(); },
                        error => { this._messageService.errorMessage(error, 'Could not Update Device'); }
                    );
                }
            },
            error => { this._messageService.errorMessage(error, 'Could not Validate Device'); }
        );
    }

    closeInvalidModelDialog() {
        this.showModelInvalidDialog = false;
        this.validationErrors = [];
    }

    returnToDeviceList() {
        this._router.navigate(['/devices/']);
    }

    handleConfigurationErrors(errorEvent: any) {
        this._messageService.errorMessage(errorEvent, 'Device measurements configuration error');
    }
}
